import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { buscarVivo } from '../../db/registroVivo'
import { DIAS_SEMANA, REGIMES_TRIBUTARIOS, novoRegistroBase } from '../../db/tipos'
import type { Cliente } from '../../db/tipos'
import { gravarLocalEEnfileirar, apagarLocalEEnfileirar } from '../../sync/outbox'
import { converterNumeroBR } from '../../lib/textoBR'
import { formatarDigitacaoMoeda } from '../../lib/moeda'
import { camposFaltandoCliente, cadastroIncompleto, ROTULO_CAMPO_CLIENTE } from '../../clientes/completude'
import { CLASSIFICACOES, ROTULO_CLASSIFICACAO } from '../../clientes/classificacao'
import VinculosRepresentadas from './VinculosRepresentadas'
import PassosDoCadastro from './PassosDoCadastro'
import {
  etapaAnterior, proximaEtapa, type EtapaCadastro,
} from '../../clientes/etapasCadastro'
import { useSalvamento } from '../../componentes/useSalvamento'
import BotaoSalvar from '../../componentes/BotaoSalvar'
import { dataISOLocal } from '../../pedidos/precos'
import { camposDoRetornoAoSalvar, lerDiasDeRetorno } from '../../visitas/retorno'
import { confirmar } from '../../componentes/confirmar'
import CampoMoeda from '../../componentes/CampoMoeda'
import TabelasDoCliente from './TabelasDoCliente'
import HistoricoCompras from './HistoricoCompras'
import { obterOuCriarPedidoAberto } from '../../pedidos/carrinho'
import AvisoClienteInativo from '../../componentes/AvisoClienteInativo'
import {
  MOTIVOS_INATIVACAO, ROTULO_MOTIVO_INATIVACAO,
  inativarCliente, reativarCliente, acoesDaFicha,
} from '../../clientes/inativacao'
import { definirDiaDeVisita } from '../../visitas/logica'
import type { MotivoInativacao } from '../../db/tipos'
import { temEntregaPropria, textoEnderecoEntrega } from '../../clientes/enderecoEntrega'
import { opcoesDeSegmento, valorNaLista, nomeNovoValido } from '../../clientes/segmentos'

// Caminhos de icone (SVG inline, sem biblioteca -- mesmo jeito do rodape).
const ICONE_EMPRESA = 'M4 21V4h10v17M14 9h6v12M8 8h2M8 12h2M8 16h2M17 13h.01M17 17h.01'
const ICONE_LOCAL = 'M12 21s-6-5.6-6-10a6 6 0 1 1 12 0c0 4.4-6 10-6 10zM12 8.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z'
const ICONE_CAMINHAO = 'M2 6h11v10H2zM13 10h4l3 3v3h-7zM6 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM17 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'
const ICONE_SACOLA = 'M5 8h14l-1 12H6zM9 8a3 3 0 0 1 6 0'
const ICONE_BALAO = 'M4 5h16v11H9l-5 4z'
const ICONE_BARRAS = 'M5 20V10M12 20V4M19 20v-7'

function Cartao({
  caminho, titulo, subtitulo, children,
}: { caminho: string; titulo: string; subtitulo?: string; children?: ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={caminho} /></svg>
        </span>
        <div className="min-w-0">
          <h3 className="font-bold text-slate-800">{titulo}</h3>
          {subtitulo && <p className="text-sm text-slate-500">{subtitulo}</p>}
        </div>
      </div>
      {children}
    </div>
  )
}

export default function FormCliente() {
  const { id } = useParams()
  const navegar = useNavigate()
  const { estado: estadoSalvamento, executar, reiniciar } = useSalvamento()
  const ehNovo = id === undefined
  const existente = useLiveQuery(() => buscarVivo(banco.clientes, id), [id])
  // Lista de segmentos do vendedor (0037, 11/10): ele adiciona e exclui aqui.
  const segmentos = useLiveQuery(() => banco.segmentos.filter((s) => s.apagado_em === null).toArray(), [])
  const nomesSegmento = (segmentos ?? []).map((s) => s.nome)

  const [campos, setCampos] = useState({
    razao_social: '', nome_fantasia: '', cnpj_cpf: '', ddd: '', telefone: '', email: '',
    endereco: '', bairro: '', municipio: '', uf: '', cep: '', regiao: '', segmento: '',
    entrega_endereco: '', entrega_bairro: '', entrega_municipio: '', entrega_uf: '', entrega_cep: '',
    inscricao_estadual: '', regime_tributario: '', linha_credito: '',
    potencial: '', dia_visita: '', dia_entrega: '', retorno_dias: '', observacoes: '',
  })

  useEffect(() => {
    if (!existente) return
    setCampos({
      razao_social: existente.razao_social,
      nome_fantasia: existente.nome_fantasia ?? '',
      cnpj_cpf: existente.cnpj_cpf ?? '',
      ddd: existente.contatos[0]?.ddd ?? '',
      telefone: existente.contatos[0]?.telefone ?? '',
      email: existente.contatos[0]?.email ?? '',
      endereco: existente.endereco ?? '',
      bairro: existente.bairro ?? '',
      municipio: existente.municipio ?? '',
      uf: existente.uf ?? '',
      cep: existente.cep ?? '',
      entrega_endereco: existente.entrega_endereco ?? '',
      entrega_bairro: existente.entrega_bairro ?? '',
      entrega_municipio: existente.entrega_municipio ?? '',
      entrega_uf: existente.entrega_uf ?? '',
      entrega_cep: existente.entrega_cep ?? '',
      regiao: existente.regiao ?? '',
      segmento: existente.segmento ?? '',
      inscricao_estadual: existente.inscricao_estadual ?? '',
      regime_tributario: existente.regime_tributario ?? '',
      // Formatado ja na abertura: sem isso a ficha mostra "5000" cru num campo
      // que promete reais, e so vira "5.000,00" se a pessoa clicar e sair dele.
      linha_credito:
        existente.linha_credito != null ? formatarDigitacaoMoeda(String(existente.linha_credito)) : '',
      potencial: existente.potencial ?? '',
      dia_visita: existente.dia_visita?.toString() ?? '',
      dia_entrega: existente.dia_entrega?.toString() ?? '',
      retorno_dias: existente.retorno_dias?.toString() ?? '',
      observacoes: existente.observacoes ?? '',
    })
  }, [existente])

  const [erroRetorno, setErroRetorno] = useState<string | null>(null)
  const [inativando, setInativando] = useState(false)
  const [motivoInativacao, setMotivoInativacao] = useState<MotivoInativacao>('mal_pagador')
  const [obsInativacao, setObsInativacao] = useState('')
  const [etapa, setEtapa] = useState<EtapaCadastro>('dados')
  const [erroRazao, setErroRazao] = useState(false)
  const [erroEmail, setErroEmail] = useState(false)
  const emailRef = useRef<HTMLInputElement>(null)
  const topoRef = useRef<HTMLDivElement>(null)
  // Endereco de entrega (11/10): o card abre a janela Idem/Novo; "Novo" abre a
  // tela de preenchimento. O que se digita la e um RASCUNHO -- so entra no
  // formulario com "Usar este endereço", e grava no Salvar, como o resto.
  const [janelaEntrega, setJanelaEntrega] = useState(false)
  const [janelaSegmento, setJanelaSegmento] = useState<null | 'adicionar' | 'gerenciar'>(null)
  const [novoSegmento, setNovoSegmento] = useState('')
  const [erroSegmento, setErroSegmento] = useState<string | null>(null)

  function abrirJanelaSegmento(modo: 'adicionar' | 'gerenciar') {
    setNovoSegmento('')
    setErroSegmento(null)
    setJanelaSegmento(modo)
  }
  async function adicionarSegmento() {
    const validacao = nomeNovoValido(novoSegmento, nomesSegmento)
    if (validacao !== 'ok') {
      setErroSegmento(validacao === 'vazio' ? 'Digite o nome do segmento.' : 'Esse segmento já está na lista.')
      return
    }
    const nome = novoSegmento.trim()
    await gravarLocalEEnfileirar('segmentos', { ...novoRegistroBase(), nome })
    // Veio do "+ Adicionar" da lista: o cliente ja fica com o segmento novo.
    if (janelaSegmento === 'adicionar') {
      setCampos((c) => ({ ...c, segmento: nome }))
      reiniciar()
      setJanelaSegmento(null)
    } else {
      setNovoSegmento('')
      setErroSegmento(null)
    }
  }
  async function excluirSegmento(idSegmento: string, nome: string) {
    const pergunta = `Excluir o segmento "${nome}" da lista?\n\nOs clientes que já têm esse segmento continuam com ele.`
    if (!(await confirmar(pergunta, { botao: 'Excluir', perigo: true }))) return
    await apagarLocalEEnfileirar('segmentos', idSegmento)
  }
  const [rascunhoEntrega, setRascunhoEntrega] = useState<null | {
    entrega_endereco: string; entrega_bairro: string; entrega_municipio: string; entrega_uf: string; entrega_cep: string
  }>(null)

  function entregaIdem() {
    setCampos((c) => ({ ...c, entrega_endereco: '', entrega_bairro: '', entrega_municipio: '', entrega_uf: '', entrega_cep: '' }))
    setJanelaEntrega(false)
    reiniciar()
  }
  function entregaNovo() {
    setRascunhoEntrega({
      entrega_endereco: campos.entrega_endereco, entrega_bairro: campos.entrega_bairro,
      entrega_municipio: campos.entrega_municipio, entrega_uf: campos.entrega_uf, entrega_cep: campos.entrega_cep,
    })
    setJanelaEntrega(false)
  }
  function usarRascunhoEntrega() {
    if (rascunhoEntrega) setCampos((c) => ({ ...c, ...rascunhoEntrega }))
    setRascunhoEntrega(null)
    reiniciar()
  }

  function irPara(destino: EtapaCadastro) {
    setEtapa(destino)
    // A etapa nova abre no topo, e nao onde o dedo parou na anterior.
    topoRef.current?.scrollIntoView?.({ block: 'start' })
  }
  const anterior = etapaAnterior(etapa)
  const seguinte = proximaEtapa(etapa)

  // Quem decide o que a ficha oferece e clientes/inativacao.ts, com teste
  // proprio: inativar NAO depende de o cliente estar sem rota.
  const acoes = existente ? acoesDaFicha(existente) : null

  function definir(campo: keyof typeof campos) {
    return (e: { target: { value: string } }) => {
      setCampos((c) => ({ ...c, [campo]: e.target.value }))
      // editou depois de salvar: o que esta na tela nao e mais o que foi gravado
      reiniciar()
    }
  }

  async function aoSalvar() {
    // Ciclo 2B: lanca em vez de `return` -- o executar() so mostra "Salvo"
    // quando a funcao termina sem erro.
    const dias = lerDiasDeRetorno(campos.retorno_dias)
    if (dias === 'invalido') {
      setErroRetorno('Use um número de 1 a 365 dias.')
      throw new Error('retorno invalido')
    }
    setErroRetorno(null)
    const registro: Cliente = {
      ...(existente ?? novoRegistroBase()),
      razao_social: campos.razao_social,
      nome_fantasia: campos.nome_fantasia || null,
      cnpj_cpf: campos.cnpj_cpf || null,
      contatos:
        campos.ddd || campos.telefone || campos.email
          ? [{ ddd: campos.ddd || undefined, telefone: campos.telefone || undefined, email: campos.email || undefined }]
          : [],
      endereco: campos.endereco || null,
      bairro: campos.bairro || null,
      municipio: campos.municipio || null,
      uf: campos.uf || null,
      cep: campos.cep || null,
      // Vazios = idem ao do cliente (clientes/enderecoEntrega.ts).
      entrega_endereco: campos.entrega_endereco.trim() || null,
      entrega_bairro: campos.entrega_bairro.trim() || null,
      entrega_municipio: campos.entrega_municipio.trim() || null,
      entrega_uf: campos.entrega_uf.trim() || null,
      entrega_cep: campos.entrega_cep.trim() || null,
      regiao: campos.regiao || null,
      // Grava o nome como esta na lista ("DISTRIBUIDORA" antigo vira "Distribuidora").
      segmento: valorNaLista(campos.segmento, nomesSegmento) || null,
      inscricao_estadual: campos.inscricao_estadual || null,
      regime_tributario: (campos.regime_tributario || null) as Cliente['regime_tributario'],
      linha_credito: campos.linha_credito ? converterNumeroBR(campos.linha_credito) : null,
      potencial: (campos.potencial || null) as Cliente['potencial'],
      dia_visita: campos.dia_visita === '' ? null : Number(campos.dia_visita),
      dia_entrega: campos.dia_entrega === '' ? null : Number(campos.dia_entrega),
      ...camposDoRetornoAoSalvar(existente, dias, dataISOLocal(new Date())),
      // O status e o historico da inativacao NAO tem campo no formulario e nao
      // podem ser perdidos ao salvar: quem grava os quatro e o fluxo de
      // inativar/reativar. Le do `existente`, NUNCA do formulario -- vindo do
      // formulario, salvar reativaria em silencio um cliente inativado. Cliente
      // novo nasce ativo.
      status: existente?.status ?? 'ativo',
      motivo_inativacao: existente?.motivo_inativacao ?? null,
      obs_inativacao: existente?.obs_inativacao ?? null,
      inativado_em: existente?.inativado_em ?? null,
      observacoes: campos.observacoes || null,
    }
    await gravarLocalEEnfileirar('clientes', registro)
    // Ao EDITAR, fica na tela para o "✓ Salvo" ser visto.
    // Ao CRIAR, volta para a LISTA -- igual ao FormProduto, que faz o mesmo
    // desde sempre. Ate 16/08/2026 esta linha ia para a ficha do cliente
    // recem-criado (`/clientes/${registro.id}`), e no smoke do beta a diferenca
    // entre as duas telas irmas foi lida como "o cliente nao salvou". Decisao
    // dela no mesmo dia: as duas se comportam igual.
    // ⚠ O preco disto: a ficha e onde moram "🛒 Novo pedido", os vinculos com
    // fornecedoras e os precos negociados, e eles so existem depois que o
    // cliente existe -- quem cadastra na rua agora tem de entrar no cliente de
    // novo para fazer o pedido. Foi dito a ela antes de mudar.
    // Ha E2E cobrando os dois lados (cadastro-volta-para-a-lista.spec.ts).
    if (ehNovo) navegar('/clientes', { replace: true })
  }

  async function aoExcluir() {
    if (!id) return
    if (!(await confirmar('Excluir este cliente?', { botao: 'Excluir', perigo: true }))) return
    await apagarLocalEEnfileirar('clientes', id)
    navegar('/clientes', { replace: true })
  }

  // ⚠ A ficha de um cliente que JA EXISTE nao pode aparecer antes de o dado
  // chegar do Dexie. Os campos nascem vazios e sao CONTROLADOS: quem mexer num
  // deles nesta janela tem a escolha APAGADA em silencio pelo re-render que
  // preenche o formulario -- e o Salvar grava vazio, com "✓ Salvo" na tela.
  //
  // Medido em 12/08 com linha do tempo, nao deduzido: 42ms depois de a ficha
  // abrir o formulario foi preenchido, 4ms depois o <select> ja lia VAZIO o
  // 'D' recem-escolhido, e a gravacao levou null. O E2E pegava 1 vez em 15.
  // No celular lento a janela e MAIOR, e o celular e o aparelho do projeto.
  if (!ehNovo && !existente) return <section className="p-4 text-slate-500">Carregando…</section>

  const linha1 = [campos.endereco, campos.bairro].filter(Boolean).join(' - ')
  const cidadeUf = [campos.municipio, campos.uf].filter(Boolean).join('/')
  const linha2 = [cidadeUf, campos.cep && `CEP ${campos.cep}`].filter(Boolean).join(' • ')
  const enderecoCompleto = linha1 || linha2 ? { linha1: linha1 || linha2, linha2: linha1 ? linha2 : '' } : null
  const entregaPropria = temEntregaPropria(campos)

  const estiloCampo = 'w-full border border-slate-300 rounded-lg px-3 py-2'
  const estiloRotulo = 'block text-sm text-slate-600 mb-1'

  return (
    <section className="p-4 space-y-4">
      <div ref={topoRef} className="bg-white rounded-xl shadow-sm p-4 space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-full bg-slate-100 text-teal-700 flex items-center justify-center shrink-0" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="10" cy="8" r="4" /><path d="M3 21c0-4 3-6 7-6M18 14v6M15 17h6" />
            </svg>
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-800 truncate">
              {ehNovo ? 'Cadastrar cliente' : campos.nome_fantasia || campos.razao_social || 'Cliente'}
            </h2>
            <p className="text-sm text-slate-500">
              {ehNovo ? 'Preencha os dados para incluir um novo cliente.' : 'Confira e atualize os dados do cliente.'}
            </p>
          </div>
        </div>
        <PassosDoCadastro atual={etapa} aoIr={irPara} />
        {!ehNovo && id && (
          <button
            type="button"
            onClick={() => {
              void obterOuCriarPedidoAberto(id).then((pedido) => navegar(`/pedidos/${pedido.id}`))
            }}
            className="w-full bg-teal-700 text-white rounded-lg px-3 py-2 text-sm"
          >
            🛒 Novo pedido
          </button>
        )}
      </div>
      {!ehNovo && existente && <AvisoClienteInativo cliente={existente} />}

      {existente && acoes?.podeReativar && (
        <button
          onClick={() => void reativarCliente(existente.id)}
          className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 text-sm font-medium"
        >
          Reativar cliente
        </button>
      )}

      {existente && acoes?.mostrarSemRota && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-xl p-3 text-sm space-y-2">
          <p><strong>Cliente sem rota.</strong> Ele não aparece em nenhuma agenda.</p>
          <div className="flex items-center gap-2">
            <label htmlFor="rota-rapida" className="shrink-0">Dia de visita:</label>
            <select
              id="rota-rapida"
              value=""
              onChange={(e) => {
                if (e.target.value === '') return
                void definirDiaDeVisita(existente.id, Number(e.target.value))
              }}
              className="flex-1 border border-amber-300 rounded-lg px-2 py-1.5 bg-white"
            >
              <option value="">Escolher…</option>
              {DIAS_SEMANA.map((dia, indice) => (
                <option key={dia} value={indice}>{dia}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {!ehNovo && existente && cadastroIncompleto(existente) && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-xl p-3 text-sm">
          <strong>Cadastro incompleto.</strong> Falta:{' '}
          {camposFaltandoCliente(existente).map((c, i) => (
            <span key={c}>
              {i > 0 && ', '}
              <strong className="text-red-600" data-testid="campo-faltando">{ROTULO_CAMPO_CLIENTE[c]}</strong>
            </span>
          ))}.
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          // Sem `required` no campo: ele mora na etapa Dados, e com a etapa
          // escondida o navegador nao consegue focar o campo e o Salvar morre
          // em silencio. A checagem e feita aqui e leva a pessoa ate o campo.
          if (!campos.razao_social.trim()) {
            setErroRazao(true)
            irPara('dados')
            return
          }
          // `noValidate` no form: com a etapa escondida o navegador recusava o
          // e-mail invalido sem conseguir mostrar o aviso, e o Salvar morria
          // calado (code review 11/10). A checagem e feita aqui, e leva a
          // pessoa ate o campo -- o mesmo caminho da razao social.
          if (emailRef.current && !emailRef.current.validity.valid) {
            setErroEmail(true)
            irPara('dados')
            return
          }
          if (lerDiasDeRetorno(campos.retorno_dias) === 'invalido') {
            setErroRetorno('Use um número de 1 a 365 dias.')
            irPara('endereco')
            return
          }
          void executar(aoSalvar)
        }}
        className="space-y-4"
        noValidate
      >
        <div hidden={etapa !== 'dados'} className="space-y-4">
          <Cartao caminho={ICONE_EMPRESA} titulo="Dados da empresa" subtitulo="Informações principais do cliente.">
            <div>
              <label htmlFor="razao" className={estiloRotulo}>Razão social <span className="text-red-600">*</span></label>
              <input
                id="razao" value={campos.razao_social} aria-invalid={erroRazao}
                onChange={(e) => { setErroRazao(false); definir('razao_social')(e) }}
                className={estiloCampo}
              />
              {erroRazao && <p className="text-xs text-red-600 mt-1">Informe a razão social para salvar.</p>}
            </div>
            <div>
              <label htmlFor="fantasia" className={estiloRotulo}>Nome fantasia</label>
              <input id="fantasia" value={campos.nome_fantasia} onChange={definir('nome_fantasia')} className={estiloCampo} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="doc" className={estiloRotulo}>CNPJ/CPF</label>
                <input id="doc" value={campos.cnpj_cpf} onChange={definir('cnpj_cpf')} className={estiloCampo} />
              </div>
              <div>
                <label htmlFor="ie" className={estiloRotulo}>Inscrição estadual</label>
                <input id="ie" value={campos.inscricao_estadual} onChange={definir('inscricao_estadual')} className={estiloCampo} placeholder="ISENTO" />
              </div>
            </div>
            <div>
              <label htmlFor="tel" className={estiloRotulo}>Telefone/WhatsApp</label>
              <div className="flex gap-2">
                <input
                  id="ddd" value={campos.ddd} onChange={definir('ddd')} placeholder="DDD"
                  inputMode="numeric" maxLength={3} className={`${estiloCampo} w-16`}
                />
                <input id="tel" value={campos.telefone} onChange={definir('telefone')} className={estiloCampo} />
              </div>
            </div>
            <div>
              <label htmlFor="mail" className={estiloRotulo}>E-mail</label>
              <input id="mail" ref={emailRef} type="email" value={campos.email} aria-invalid={erroEmail}
                onChange={(e) => { setErroEmail(false); definir('email')(e) }} className={estiloCampo} />
              {erroEmail && <p className="text-xs text-red-600 mt-1">Confira o e-mail (exemplo: nome@empresa.com).</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="seg" className={estiloRotulo}>Segmento</label>
                  <button type="button" onClick={() => abrirJanelaSegmento('gerenciar')}
                    aria-label="Editar lista de segmentos" className="text-teal-700 text-sm font-medium underline mb-1 px-1">Editar</button>
                </div>
                <select
                  id="seg" value={valorNaLista(campos.segmento, nomesSegmento)} className={estiloCampo}
                  onChange={(e) => {
                    if (e.target.value === '__novo__') { abrirJanelaSegmento('adicionar'); return }
                    // Ela nao achou o lapis (11/10): excluir tambem se abre pela lista.
                    if (e.target.value === '__editar__') { abrirJanelaSegmento('gerenciar'); return }
                    definir('segmento')(e)
                  }}
                >
                  <option value="">—</option>
                  {opcoesDeSegmento(nomesSegmento, campos.segmento).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                  <option value="__novo__">+ Adicionar segmento</option>
                  <option value="__editar__">✎ Editar segmentos (excluir)</option>
                </select>
              </div>
              <div>
                <label htmlFor="pot" className={estiloRotulo}>Classificação</label>
                <select id="pot" value={campos.potencial} onChange={definir('potencial')} className={estiloCampo}>
                  <option value="">—</option>
                  {CLASSIFICACOES.map((c) => (
                    <option key={c} value={c}>{ROTULO_CLASSIFICACAO[c]}</option>
                  ))}
                </select>
              </div>
            </div>
          </Cartao>
          {!ehNovo && id && <VinculosRepresentadas clienteId={id} />}
        </div>

        <div hidden={etapa !== 'endereco'} className="space-y-4">
          <Cartao caminho={ICONE_LOCAL} titulo="Endereço" subtitulo="Informe o endereço para entrega e visitas.">
            <div>
              <label htmlFor="end" className={estiloRotulo}>Endereço</label>
              <input id="end" value={campos.endereco} onChange={definir('endereco')} className={estiloCampo} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="cep" className={estiloRotulo}>CEP</label>
                <input id="cep" value={campos.cep} onChange={definir('cep')} className={estiloCampo} />
              </div>
              <div>
                <label htmlFor="bairro" className={estiloRotulo}>Bairro</label>
                <input id="bairro" value={campos.bairro} onChange={definir('bairro')} className={estiloCampo} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label htmlFor="mun" className={estiloRotulo}>Município</label>
                <input id="mun" value={campos.municipio} onChange={definir('municipio')} className={estiloCampo} />
              </div>
              <div>
                <label htmlFor="uf" className={estiloRotulo}>UF</label>
                <input id="uf" maxLength={2} value={campos.uf} onChange={definir('uf')} className={estiloCampo} />
              </div>
            </div>
            <div>
              <label htmlFor="regiao" className={estiloRotulo}>Região</label>
              <input id="regiao" value={campos.regiao} onChange={definir('regiao')} className={estiloCampo} />
            </div>
          </Cartao>

          <Cartao caminho={ICONE_CAMINHAO} titulo="Informações logísticas" subtitulo="Esses dados ajudam no planejamento de rota e entrega.">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="visita" className={estiloRotulo}>Dia de visita</label>
                <select id="visita" value={campos.dia_visita} onChange={definir('dia_visita')} className={estiloCampo}>
                  <option value="">—</option>
                  {DIAS_SEMANA.map((dia, indice) => (
                    <option key={dia} value={indice}>{dia}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="entrega" className={estiloRotulo}>Dia de entrega</label>
                <select id="entrega" value={campos.dia_entrega} onChange={definir('dia_entrega')} className={estiloCampo}>
                  <option value="">—</option>
                  {DIAS_SEMANA.map((dia, indice) => (
                    <option key={dia} value={indice}>{dia}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="retorno" className={estiloRotulo}>Retorno a cada (dias)</label>
              <input id="retorno" inputMode="numeric" placeholder="ex.: 45" value={campos.retorno_dias}
                onChange={definir('retorno_dias')} className={estiloCampo} />
              {erroRetorno && <p className="text-xs text-red-600 mt-1">{erroRetorno}</p>}
            </div>
          </Cartao>

          <button
            type="button" onClick={() => setJanelaEntrega(true)} data-testid="card-entrega"
            className="w-full text-left bg-teal-50 rounded-xl p-3 flex gap-3 items-start"
          >
            <span className="text-teal-700 shrink-0" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={ICONE_CAMINHAO} /></svg>
            </span>
            <span className="text-sm min-w-0 flex-1">
              <span className="block font-semibold text-teal-800">Endereço de entrega</span>
              {entregaPropria ? (
                <span className="block font-medium text-slate-800 break-words">{textoEnderecoEntrega(campos)}</span>
              ) : (
                <>
                  <span className="block text-slate-600">Idem ao do cliente</span>
                  {enderecoCompleto && <span className="block text-slate-500 break-words">{enderecoCompleto.linha1}{enderecoCompleto.linha2 && ` • ${enderecoCompleto.linha2}`}</span>}
                </>
              )}
            </span>
            <span className="text-teal-700 shrink-0 self-center" aria-hidden="true">›</span>
          </button>
        </div>

        <div hidden={etapa !== 'comercial'} className="space-y-4">
          <Cartao caminho={ICONE_SACOLA} titulo="Condições comerciais" subtitulo="Defina as condições e parâmetros de venda deste cliente.">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="regime" className={estiloRotulo}>Regime tributário</label>
                <select id="regime" value={campos.regime_tributario} onChange={definir('regime_tributario')} className={estiloCampo}>
                  <option value="">—</option>
                  {REGIMES_TRIBUTARIOS.map((r) => (
                    <option key={r.codigo} value={r.codigo}>{r.rotulo}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="credito" className={estiloRotulo}>Linha de crédito (R$)</label>
                <CampoMoeda
                  id="credito" value={campos.linha_credito}
                  onChange={(texto) => definir('linha_credito')({ target: { value: texto } })}
                  className={estiloCampo}
                />
              </div>
            </div>
          </Cartao>
          {!ehNovo && id && <TabelasDoCliente clienteId={id} />}
          {/* O campo Status SAIU do formulario por decisao dela (11/08): ele era
              uma segunda porta que criava inativo SEM motivo e SEM data, e a
              tarja vermelha saia vazia. Quem mexe no status agora sao os botoes
              de inativar/reativar, que exigem o motivo. */}
          <Cartao caminho={ICONE_BALAO} titulo="Observações">
            <textarea id="obs" aria-label="Observações" value={campos.observacoes} onChange={definir('observacoes')} className={estiloCampo} rows={3} />
          </Cartao>
        </div>

        <div hidden={etapa !== 'historico'} className="space-y-4">
          {!ehNovo && id ? (
            <>
              <HistoricoCompras clienteId={id} />
            </>
          ) : (
            <Cartao caminho={ICONE_BARRAS} titulo="Histórico" subtitulo="As compras deste cliente aparecem aqui depois do primeiro pedido." />
          )}
        </div>

        <div className="flex gap-3">
          {anterior && (
            <button
              type="button" onClick={() => irPara(anterior)}
              className="flex-1 border border-teal-700 text-teal-700 rounded-lg py-2 font-medium bg-white"
            >
              ← Voltar
            </button>
          )}
          {seguinte && (
            <button
              type="button" onClick={() => irPara(seguinte)}
              className="flex-1 bg-teal-700 text-white rounded-lg py-2 font-medium"
            >
              Próximo →
            </button>
          )}
        </div>
        {/* Historico e so consulta (ela, 11/10): sem Salvar nessa etapa. */}
        {etapa !== 'historico' && <BotaoSalvar estado={estadoSalvamento}>Salvar</BotaoSalvar>}
      </form>

      {/* Inativar mora AQUI, e nao dentro da tarja de "sem rota": cliente com
          dia de visita marcado -- a maioria -- nunca veria aquela tarja, e desde
          que o <select> de status saiu ficaria sem forma nenhuma de sair de
          circulacao. Fica junto do excluir porque e a mesma familia de decisao. */}
      {existente && acoes?.podeInativar && (
        <div className="bg-white rounded-xl shadow-sm p-3 space-y-2">
          {!inativando ? (
            <button
              onClick={() => setInativando(true)}
              className="w-full border border-amber-600 text-amber-800 rounded-lg py-1.5 text-sm"
            >
              Marcar como inativo
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-700">Por que este cliente saiu de circulação?</p>
              <select
                aria-label="Motivo da inativação"
                value={motivoInativacao}
                onChange={(e) => setMotivoInativacao(e.target.value as MotivoInativacao)}
                className="w-full border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
              >
                {MOTIVOS_INATIVACAO.map((m) => (
                  <option key={m} value={m}>{ROTULO_MOTIVO_INATIVACAO[m]}</option>
                ))}
              </select>
              <input
                value={obsInativacao}
                onChange={(e) => setObsInativacao(e.target.value)}
                placeholder="Detalhe (opcional)"
                className="w-full border border-slate-300 rounded-lg px-2 py-1.5"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setInativando(false)}
                  className="flex-1 border border-slate-300 text-slate-600 rounded-lg py-1.5 text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    void inativarCliente(existente.id, motivoInativacao, obsInativacao || null)
                      .then(() => { setInativando(false); setObsInativacao('') })
                  }}
                  className="flex-1 bg-red-600 text-white rounded-lg py-1.5 text-sm"
                >
                  Confirmar inativação
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!ehNovo && (
        <button onClick={() => void aoExcluir()} className="w-full text-red-600 text-sm underline py-2">
          Excluir cliente
        </button>
      )}

      {janelaSegmento && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setJanelaSegmento(null)}>
          <div
            role="dialog" aria-modal="true" aria-label="Segmentos"
            className="w-full bg-white rounded-t-2xl p-4 pb-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
          >
            <p className="text-base font-semibold text-slate-800">
              {janelaSegmento === 'adicionar' ? 'Adicionar segmento' : 'Segmentos'}
            </p>
            <div>
              <label htmlFor="seg-novo" className={estiloRotulo}>Novo segmento</label>
              <div className="flex gap-2">
                <input id="seg-novo" value={novoSegmento} maxLength={60} autoFocus={janelaSegmento === 'adicionar'}
                  onChange={(e) => { setNovoSegmento(e.target.value); setErroSegmento(null) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void adicionarSegmento() } }}
                  className={estiloCampo} />
                <button type="button" onClick={() => void adicionarSegmento()}
                  className="bg-teal-700 text-white rounded-lg px-4 font-semibold shrink-0">
                  Adicionar
                </button>
              </div>
              {erroSegmento && <p className="text-xs text-red-600 mt-1">{erroSegmento}</p>}
            </div>
            {janelaSegmento === 'gerenciar' && (
              <ul className="divide-y divide-slate-100" data-testid="lista-segmentos">
                {[...(segmentos ?? [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((s) => (
                  <li key={s.id} className="flex items-center justify-between py-2">
                    <span className="text-slate-800">{s.nome}</span>
                    <button type="button" onClick={() => void excluirSegmento(s.id, s.nome)}
                      aria-label={`Excluir segmento ${s.nome}`} className="text-red-600 px-2">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" onClick={() => setJanelaSegmento(null)}
              className="w-full rounded-lg border border-slate-300 py-3 font-semibold text-slate-700">
              Fechar
            </button>
          </div>
        </div>
      )}

      {janelaEntrega && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setJanelaEntrega(false)}>
          <div
            role="dialog" aria-modal="true" aria-label="Endereço de entrega"
            className="w-full bg-white rounded-t-2xl p-4 pb-6 space-y-4" onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-1">
              <p className="text-base font-semibold text-slate-800">Endereço de entrega</p>
              <p className="text-sm text-slate-600">A entrega vai para o mesmo endereço do cliente ou para outro?</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={entregaIdem}
                className="rounded-lg border border-teal-700 py-3 font-semibold text-teal-700">
                Idem
              </button>
              <button type="button" onClick={entregaNovo}
                className="rounded-lg bg-teal-700 py-3 font-semibold text-white">
                Novo
              </button>
            </div>
            <button type="button" onClick={() => setJanelaEntrega(false)}
              className="w-full text-sm text-slate-500 py-1">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {rascunhoEntrega && (
        <div role="dialog" aria-modal="true" aria-label="Novo endereço de entrega"
          className="fixed inset-0 z-50 bg-slate-50 overflow-y-auto">
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setRascunhoEntrega(null)}
                className="text-teal-700 text-sm font-medium">
                ‹ Voltar
              </button>
            </div>
            <Cartao caminho={ICONE_CAMINHAO} titulo="Endereço de entrega" subtitulo="Para onde a mercadoria deste cliente vai.">
              <div>
                <label htmlFor="ent-end" className={estiloRotulo}>Endereço</label>
                <input id="ent-end" value={rascunhoEntrega.entrega_endereco} className={estiloCampo}
                  onChange={(e) => setRascunhoEntrega({ ...rascunhoEntrega, entrega_endereco: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="ent-cep" className={estiloRotulo}>CEP</label>
                  <input id="ent-cep" value={rascunhoEntrega.entrega_cep} className={estiloCampo}
                    onChange={(e) => setRascunhoEntrega({ ...rascunhoEntrega, entrega_cep: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="ent-bairro" className={estiloRotulo}>Bairro</label>
                  <input id="ent-bairro" value={rascunhoEntrega.entrega_bairro} className={estiloCampo}
                    onChange={(e) => setRascunhoEntrega({ ...rascunhoEntrega, entrega_bairro: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label htmlFor="ent-mun" className={estiloRotulo}>Município</label>
                  <input id="ent-mun" value={rascunhoEntrega.entrega_municipio} className={estiloCampo}
                    onChange={(e) => setRascunhoEntrega({ ...rascunhoEntrega, entrega_municipio: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="ent-uf" className={estiloRotulo}>UF</label>
                  <input id="ent-uf" maxLength={2} value={rascunhoEntrega.entrega_uf} className={estiloCampo}
                    onChange={(e) => setRascunhoEntrega({ ...rascunhoEntrega, entrega_uf: e.target.value })} />
                </div>
              </div>
            </Cartao>
            <button type="button" onClick={usarRascunhoEntrega}
              className="w-full bg-teal-700 text-white rounded-lg py-3 font-semibold">
              Usar este endereço
            </button>
            <p className="text-xs text-slate-500 text-center">Depois, toque em Salvar no cadastro para gravar.</p>
          </div>
        </div>
      )}
    </section>
  )
}
